import React from 'react';
import { Briefcase, Calendar, CheckCircle, ChevronRight } from 'lucide-react';

const missionLog = [
  {
    id: 1,
    title: "DevOps Engineer",
    company: "Confidential Organization",
    duration: "2025 – Present",
    current: true,
    color: "#3b82f6",
    description: "Leading cloud automation, Kubernetes orchestration, CI/CD optimization, infrastructure as code, and strengthening security posture across production environments.",
    highlights: [
      "Architecting secure multi-cloud infrastructure",
      "Implementing GitOps workflows for continuous delivery",
      "Leading security-first DevSecOps practices",
      "Optimizing CI/CD pipelines for rapid deployment"
    ]
  },
  {
    id: 2,
    title: "DevOps Engineer",
    company: "Anblicks",
    duration: "2020 – 2025",
    current: false,
    color: "#10b981",
    description: "Drove cloud transformation initiatives and established DevSecOps best practices across enterprise environments.",
    highlights: [
      "Deployed production Kubernetes clusters (EKS/AKS) with 99.99% uptime",
      "Reduced deployment time by 70% through GitOps automation",
      "Led AWS-to-Azure migration with zero downtime",
      "Automated 500+ infrastructure resources with Terraform",
      "Reduced security vulnerabilities by 85%",
      "Designed multi-region disaster recovery solutions"
    ]
  },
  {
    id: 3,
    title: "AWS Solutions Architect",
    company: "Discus Business Solutions",
    duration: "2019 – 2020",
    current: false,
    color: "#f59e0b",
    description: "Managed AWS infrastructure and led cloud migration initiatives for multiple enterprise clients.",
    highlights: [
      "Architected scalable AWS solutions for enterprise workloads",
      "Managed EC2, RDS, S3, and Lambda infrastructure",
      "Implemented automated backup and disaster recovery solutions",
      "Configured VPC networking, security groups, and IAM policies",
      "Led on-premises to AWS cloud migrations",
      "Created CloudWatch monitoring dashboards and alerting systems"
    ]
  }
];

const MissionCard = ({ mission, index }) => {
  return (
    <div className="relative">
      {/* Timeline connector */}
      {index > 0 && (
        <div className="absolute left-5 -top-6 w-0.5 h-6 bg-gradient-to-b from-purple-200 to-purple-300" />
      )}
      
      <div 
        className={`relative bg-white rounded-2xl p-6 shadow-md border-l-4 hover:shadow-lg transition-all duration-300`}
        style={{ borderLeftColor: mission.color }}
      >
        {/* Current badge */}
        {mission.current && (
          <div className="absolute -top-2.5 right-5 px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full text-white text-xs font-bold shadow-md">
            Active Mission
          </div>
        )}

        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0"
              style={{ backgroundColor: `${mission.color}12` }}
            >
              <Briefcase className="w-5 h-5" style={{ color: mission.color }} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-800">{mission.title}</h3>
              <p className="text-base font-semibold" style={{ color: mission.color }}>{mission.company}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-gray-500 text-sm font-medium bg-gray-50 px-3 py-1 rounded-full">
            <Calendar className="w-3.5 h-3.5" />
            {mission.duration}
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-600 text-sm mb-4 leading-relaxed">
          {mission.description}
        </p>

        {/* Highlights */}
        <div className="space-y-2">
          {mission.highlights.map((highlight, i) => (
            <div 
              key={i} 
              className="flex items-start gap-2 text-sm"
            >
              <CheckCircle className="w-4 h-4 mt-0.5 text-emerald-500 flex-shrink-0" />
              <span className="text-gray-700">{highlight}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ExperienceSection = () => {
  return (
    <section id="experience" className="py-16 bg-gradient-to-b from-white via-purple-50/20 to-white relative overflow-hidden">
      {/* Background */}
      <div className="absolute top-16 right-8 text-5xl opacity-[0.03]">🚀</div>
      <div className="absolute bottom-16 left-8 text-5xl opacity-[0.03]">📋</div>
      
      <div className="max-w-3xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white shadow-sm border border-purple-100 mb-3">
            <Briefcase className="w-4 h-4 text-purple-600" />
            <span className="font-bold text-purple-600 text-sm">Career Journey</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-gray-800 mb-2">
            Mission <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">Log</span>
          </h2>
          <p className="text-gray-500 text-sm">
            Building secure, scalable infrastructure at enterprise scale
          </p>
        </div>

        {/* Mission Timeline */}
        <div className="space-y-6">
          {missionLog.map((mission, index) => (
            <MissionCard key={mission.id} mission={mission} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;
